/**
 * Read an image file and return a small PNG data URI (resized to a max height),
 * so an agent's logo/headshot stays compact when stored on report/account
 * branding and rendered into the PDF. Rejects non-images and oversized files.
 *
 * Used by both the report editor's logo upload and the dashboard broker-profile
 * uploads. The backend re-validates the result as a raster image data URI.
 *
 * @param {File} file
 * @param {number} [maxH] Max output height in pixels (default 200).
 * @returns {Promise<string>}
 */
export const fileToImageDataUrl = (file, maxH = 200) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return reject(new Error("Please choose an image file."));
    if (file.size > 5 * 1024 * 1024) return reject(new Error("Image is too large (max 5MB)."));
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxH / (img.height || maxH));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Couldn't process the image."));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("That image couldn't be read."));
      img.src = /** @type {string} */ (reader.result);
    };
    reader.onerror = () => reject(new Error("Couldn't read the file."));
    reader.readAsDataURL(file);
  });
